import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import Actions from '../actions/Actions';
import styles from './Slide.module.scss';
import { Document, Page } from 'react-pdf';
import 'react-pdf/dist/Page/TextLayer.css';
import Spinner from '@components/spinner/Spinner';
import Button from '@components/button/Button';
import { downloadFile, getMediaAbsolutePath } from '@services/File';
import { useStandaloneContent } from '@src/hooks/useStandaloneContent';
import RotateIcon from '@src/assets/icons/rotate.svg?react';
import { isHomeEDApp } from '@src/constants';
import { useMarkLessonComplete } from '@src/hooks';
import { isMobile } from 'react-device-detect';
import AutoCompleteModal from '@src/components/program-details/complete-modal/AutoCompleteModal';
import { resetGoogleTranslateWidget } from '@src/utils/helpers';
import FSBackIcon from 'src/assets/icons/arrow_back.svg?react';
import DownloadIcon from 'src/assets/icons/material_download.svg?react';
import PrintIcon from 'src/assets/icons/material_print.svg?react';
import ZoomInIcon from 'src/assets/icons/material_zoom_in.svg?react';
import ZoomOutIcon from 'src/assets/icons/material_zoom_out.svg?react';
import FullscreenIcon from 'src/assets/icons/material_full.svg?react';
import FSExitIcon from 'src/assets/icons/fullscreen_exit.svg?react';
import logger from '@src/utils/logger';


type Props = {
  data: string;
  isFullScreen: boolean;
  isPreview?: boolean;
  goBack?: () => void;
  pageNumber: number;
  changePageNumber: (value: number) => void;
  startLesson?: () => void;
  goFullscreen?: () => void;
};

const roundScale = (num: number) => Math.round(num * 100) / 100;

function MaterialSlide(props: Props) {
  const { data, isFullScreen, isPreview, goBack, pageNumber, changePageNumber, goFullscreen } = props;
  
  const {
    content, totalPages, setTotalPages,
    accountLocked, lesson, programName
  } = useStandaloneContent();
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState<number | undefined>(undefined);
  const [height, setHeight] = useState<number | undefined>(undefined);
  const [completeModal, setCompleteModal] = useState(false);

  const [PDFViewerOneError, setPDFViewerOneError] = useState(false);
  const [scale, setScale] = useState(0.9);
  const [downloading, setDownloading] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  const onMarkLessonCompleted = useCallback(() => {
    setCompleteModal(false);
  }, []);

  const handleBack = useCallback(() => {
    resetGoogleTranslateWidget();
    goBack?.();
  }, [goBack])

  const { sllMarkLessonComplete, loading: isMarkLessonCompleteLoading } = useMarkLessonComplete(onMarkLessonCompleted, handleBack, undefined);

  useEffect(() => {
    setTotalPages(1);
  }, [data, setTotalPages]);

  const fileObj = useMemo(() => {
    return getMediaAbsolutePath(data);
  }, [data]);

  const onDocumentLoadSuccess = useCallback(({ numPages }) => {
    setTotalPages(numPages);
    if (content?.lastInLesson && (!isHomeEDApp || !accountLocked) && !isPreview) {
      setTimeout(() => {
        setCompleteModal(true);
        setPDFViewerOneError(false);
      }, 3500);
    }
  }, [setTotalPages, content?.lastInLesson, accountLocked, isPreview]);

    useEffect(() => {
      if (fileObj) {
        // Use a timeout to avoid blocking the main thread
        const fetchTimer = setTimeout(() => {
          fetch(fileObj)
            .then(resp => {
              if (!resp.ok) {
                throw new Error(`HTTP error! Status: ${resp.status}`);
              }
              return resp.blob();
            })
            .then(blob => {
              logger.debug('[Material Slide Viewer] Slide fetched successfully, size:', `${Math.round(blob.size / 1024 / 1024)}MB`);
              const newPDFBlob = new Blob([blob], {type: 'application/pdf'});
              const localUrl = window.URL.createObjectURL(newPDFBlob);
              const iframeEle = document.getElementById("hiddenFileHackForPrint");
              if (iframeEle) {
                // @ts-ignore
                iframeEle.src = localUrl;
              }
              window.URL.revokeObjectURL(localUrl);
            }).catch((e) => {
              logger.error('[Material Slide Viewer] Error fetching PDF:', e);
            });
        }, 0);
        
        return () => clearTimeout(fetchTimer);
      }
    }, [fileObj])

  const calculateSize = useCallback(() => {
    if (wrapperRef.current) {
      const { width, height } = wrapperRef.current.getBoundingClientRect();
      setWidth(width);
      setHeight(height);
    }
  }, []);

  const increaseScale = useCallback(() => {
    const newScale = Math.min(scale + 0.1, 2);
    setScale(roundScale(newScale));
  }, [scale]);

  const decreaseScale = useCallback(() => {
    const newScale = Math.max(scale - 0.1, 0.1);
    setScale(roundScale(newScale));
  }, [scale]);

  const download = useCallback(() => {
    if (data) {
      setDownloading(true);
      downloadFile(data, 'pdf')
        .catch(() => {
          alert(`Can't download file.`);
        })
        .finally(() => {
          setDownloading(false);
        });
    }
  }, [data]);

  const printFile = () => {
    let getPrintEle = document.getElementById("hiddenFileHackForPrint");

    setTimeout(() => {
      if (getPrintEle?.getAttribute("src") === null) {
        throw new Error('PDF not loaded');
      }
      (getPrintEle as HTMLIFrameElement)?.contentWindow?.print();
    }, 500)
  }

  const handleFullscreen = () => {
    goFullscreen?.()
  }

  useEffect(() => {
    window.addEventListener('orientationchange', calculateSize);

    return () => {
      window.removeEventListener('orientationchange', calculateSize);
    };
  }, [calculateSize])

  useLayoutEffect(() => {
    window.addEventListener('resize', calculateSize);

    calculateSize();
    return () => {
      window.removeEventListener('resize', calculateSize);
    };
  }, [calculateSize]);

  useEffect(() => {
    if (width) {
      setTimeout(calculateSize, 50);
    }
  }, [calculateSize, width]);

  const prevDisabled = useMemo(() => pageNumber <= 1, [pageNumber]);
  const isLast = useMemo(() => pageNumber >= totalPages, [pageNumber, totalPages]);

  const handleCompleteLesson = useCallback(async () => {
    if (lesson) await sllMarkLessonComplete(lesson, programName);
    resetGoogleTranslateWidget();
  }, [lesson, programName, sllMarkLessonComplete]);

  const handleLeft = useCallback(() => {
    changePageNumber(Math.max(pageNumber - 1, 1));
  }, [pageNumber, changePageNumber]);

  const handleRight = useCallback(() => {
    changePageNumber(Math.min(pageNumber + 1, totalPages));
  }, [totalPages, pageNumber, changePageNumber]);

  const prevButton = useMemo(() => {
    if (prevDisabled) return null;
    return <Button size={'extraSmall'} onClick={handleLeft} className={styles.previousBtn} label={'PREVIOUS'}/>;
  }, [prevDisabled, handleLeft]);

  const nextButton = useMemo(() => {
    if (isLast) return null;
    return <Button size={'extraSmall'} onClick={handleRight} label={'NEXT'}/>;
  }, [isLast, handleRight]);

  const ratio = useMemo(() => {
    if (width && height) {
      return width/height
    }
    return 1
  }, [width, height])

  if (!fileObj) return null;

  return (
    <Actions fullScreen={isFullScreen} leftButton={prevButton} rightButton={nextButton} totalPages={totalPages} pageNumber={pageNumber} isPreview={true} goBack={goBack}>
      <div className={styles.materialSlide} ref={wrapperRef}>
        <iframe id="hiddenFileHackForPrint" title="hiddenFileHackForPrint" style={{display: "none"}}/>
        <div className={styles.topBar}>
        <div className={styles.menuBar}>
          <button onClick={goBack} className={styles.closeButton} type={'button'}>
            <div className={styles.menuWrapper}>
            <FSBackIcon className={styles.closeIcon}/>
            </div>
          </button>
        </div>
        <div className={styles.toolbar}>
          {
          <div className={styles.backTipView} style={{display: isHovered ? 'flex': 'none'}}>
            <div className={styles.arrowUp}></div>
            <div className={styles.tipTextView}>
              <div className={styles.tipTitle}>Download PDF</div>
            </div>
          </div>
          }
          <div onMouseOver={() => setIsHovered(true)} onMouseLeave={() => setIsHovered(false)}>
            <Button label='' disabled={downloading} onClick={download} className={styles.toolbarBtn} leftIcon={<DownloadIcon />}/>
          </div>
          {!PDFViewerOneError && <>
          <Button label='' disabled={downloading} onClick={printFile} className={styles.toolbarBtn} leftIcon={<PrintIcon />}/>
          <Button label='' className={styles.toolbarBtn} onClick={handleFullscreen} leftIcon={isFullScreen ? <FSExitIcon fill='#3C595C'/> : <FullscreenIcon />} />
          <Button label='' className={styles.toolbarBtn} onClick={decreaseScale} leftIcon={<ZoomOutIcon />} />
          <Button label='' className={styles.toolbarBtn} onClick={increaseScale} leftIcon={<ZoomInIcon />} />
          <div className={styles.scaleTitle}>{Number(scale * 100).toFixed(0)}%</div>
          </>}
        </div>
      </div>
        <div className={styles.materialContent} id="pdfWrapper">
          <Document
            file={fileObj}
            onLoadSuccess={onDocumentLoadSuccess}
            className={isPreview ? styles.previewDocument : styles.document}
            loading={<div className={styles.loading} style={{height: isMobile ? 300 : 500}}><Spinner/></div>}
          >
            <Page loading={<div className={styles.loading}><Spinner/></div>} pageNumber={pageNumber} width={ratio > 1.7 ? 0 : width} height={height} className={isPreview ? styles.roundCanvas : ''} scale={scale}/>
          </Document>
        </div>
        <div className={styles.rotate}>
          <RotateIcon width="60" height="60" className={styles.rorateIcon}/>
          <div className={styles.rotateTitle}>Rotate your phone,</div>
          <div className={styles.rotateDetail}>for a better reading experience.</div>
        </div>
      </div>
      {completeModal ? (
        <AutoCompleteModal open={completeModal} loading={isMarkLessonCompleteLoading} onClose={() => setCompleteModal(false)} onComplete={handleCompleteLesson} />
      ) : null}
    </Actions>
  );
}

export default MaterialSlide;
